import { effect, inject, Injectable, signal } from '@angular/core';
import { BinderAssembly } from 'src/app/models/binder-assembly.model';
import { CategoryBinderAssembly } from 'src/app/models/category-binder-assembly';
import { catchError, combineLatest, Observable, of, tap, throwError } from 'rxjs';
import { AppStateService } from './app-state.service';
import { HttpClient } from '@angular/common/http';
import { CategoriesService } from '../api/categories.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BinderAssembliesService {
  private categoriesService = inject(CategoriesService);
  private appStateService = inject(AppStateService);
  private http = inject(HttpClient);

  binderAssemblies = signal<BinderAssembly[]>([]);
  groupedAssemblies = signal<CategoryBinderAssembly[]>([]);
  searchedAssemblies = signal<BinderAssembly[]>([]);
  isFounders = signal(false);
  optionsTotal = signal(0);
  optionsSum = signal(0);
  options = signal<BinderAssembly[]>([]);
  clonedAssemblies: { [s: string]: CategoryBinderAssembly } = {};

  private baseUrl = environment.landmark_API_URL + '/binderassemblies';

  constructor(
  ) {
    effect(() => {
      const categories = this.categoriesService.categories();
      const assemblies = this.binderAssemblies();

      if (categories && assemblies) {
        if (this.isFounders()) {
          this.setGroupedAssemblies(this.groupAssembliesforHigharc(assemblies, categories));
        } else {
          this.setGroupedAssemblies(this.groupAssembliesByCategory(assemblies, categories));
        }
      }
    });
  }

  setIsFounders(isFounders: boolean) {
    this.isFounders.set(isFounders);
  }

  setGroupedAssemblies(assemblies: CategoryBinderAssembly[]) {

    this.groupedAssemblies.set(assemblies);
  }

  setBinderAssemblies(assemblies: BinderAssembly[]) {
    this.binderAssemblies.set(assemblies);
  }

  private groupAssembliesByCategory(assemblies: BinderAssembly[], categories: any[]) {
    if (!categories) return [];
  
    const grouped = categories.map(category => {
      const categoryAssemblies = assemblies.filter(a => a.CategoryID == category.CategoryID);
  
      const total = categoryAssemblies.reduce((sum, assembly) => {
        const qty = assembly.Quantity != null ? parseFloat(assembly.Quantity.toString()) : 0;
        const price = assembly.Price != null ? parseFloat(assembly.Price.toString()) : 0;
        return sum + (qty * price);
      }, 0);
  
      const optionsCount = categoryAssemblies.filter(
        a => a.Type === 'O' || (a.Price != null && parseFloat(a.Price.toString()) > 0)
      ).length;
  
      return {
        ...category,
        assemblies: categoryAssemblies,
        optionsCount,
        total
      };
    });
  
    return grouped;
  }
  

  searchAssemblies(searchTerm: string) {
    const filteredAssemblies = this.binderAssemblies().filter(assembly => {
      const name = assembly.Name ? assembly.Name.toLowerCase() : '';
      const description = assembly.Description ? assembly.Description.toLowerCase() : '';
      return name.includes(searchTerm.toLowerCase()) || description.includes(searchTerm.toLowerCase());
    }).map(assembly => {
      return {
        ...assembly,
        Description: this.getHighlightedSnippet(assembly.Description, searchTerm)
      };
    });
    this.searchedAssemblies.set(filteredAssemblies);
  }

  getHighlightedSnippet(description: string, searchTerm: string): string {
    const term = searchTerm.toLowerCase();
    const index = description.toLowerCase().indexOf(term);
    if (index === -1) {
      return description;
    }

    const snippetLength = 90;
    const start = Math.max(0, index - Math.floor(snippetLength / 2));
    const end = Math.min(description.length, start + snippetLength);

    let snippet = description.substring(start, end);
    if (start > 0) {
      snippet = '...' + snippet;
    }
    if (end < description.length) {
      snippet = snippet + '...';
    }

    const highlightedSnippet = snippet.replace(new RegExp(`(${searchTerm})`, 'gi'), '<b>$1</b>');
    return highlightedSnippet;
  }

  groupAssembliesforHigharc(assemblies: BinderAssembly[], categories: any[]) {
    const higharcCategory = {
      CategoryID: 0,
      Name: 'Selected Options',
      Description: 'Selected Options',
      Sequence: 0
    }
    if (!categories) {
      return [];
    }
    const grouped = categories.map(category => {
      return {
        ...category,
        assemblies: assemblies.filter(a => a.CategoryID == category.CategoryID && a.haId === null && !a.hidden)
      }
    });
    const higharcAssemblies = assemblies.filter(a => a.haId !== null && !a.hidden)
    if (higharcAssemblies.length > 0) {
      grouped.push({
        ...higharcCategory,
        assemblies: higharcAssemblies
      });
    }
    return grouped.sort((a, b) => a.Sequence - b.Sequence);
  }

  removeAssembly(BAID: number) {
    if(this.appStateService.learningMode()) {
      return this.removeAndReturnAssembly(BAID);
    } else {
      return this.deleteBinderAssembly(BAID).pipe(
        tap((deleted: BinderAssembly) => {
          this.removeAssemblyLocally(BAID);
      }),
      catchError(error => {
        console.error('Error removing assembly:', error);
          return throwError(() => new Error('Error removing assembly'));
        })
      );
    }
  }

  addAssembly(newAssemblyData: any) {
    return this.addBinderAssembly(newAssemblyData).pipe(
      tap((newAssembly: BinderAssembly) => {
        this.addAssemblyLocally(newAssembly);
      }),
      catchError(error => {
        console.error('Error adding assembly:', error);
        return throwError(() => new Error('Error adding assembly'));
      })
    );
  }

updateAssemblyLocally(BAID: number, updateData: any) {
  const currentAssemblies = this.binderAssemblies();
  const index = currentAssemblies.findIndex((a: any) => a.BAID === BAID);
  if (index !== -1) {
      const updatedAssemblies = [...currentAssemblies];
      updatedAssemblies[index] = updateData;
      this.binderAssemblies.set(updatedAssemblies);
  }
}

  removeAssemblyLocally(BAID: number) {
    // console.log('removing assembly:', BAID);
    const currentAssemblies = this.binderAssemblies();
    const updatedAssemblies = currentAssemblies.filter((a: any) => a.BAID !== BAID);
    this.binderAssemblies.set(updatedAssemblies);
  }

  removeAndReturnAssembly(BAID: number): Observable<BinderAssembly> {
    const currentAssemblies = this.binderAssemblies();
    const assemblyToRemove = currentAssemblies.find((a: any) => a.BAID === BAID);
    const updatedAssemblies = currentAssemblies.filter((a: any) => a.BAID !== BAID);
    this.binderAssemblies.set(updatedAssemblies);
    return of(assemblyToRemove as BinderAssembly);
  }

  addAssemblyLocally(newAssemblyData: any) {
    const currentAssemblies = this.binderAssemblies();
    this.binderAssemblies.set([...currentAssemblies, newAssemblyData]);
  }

  updateAssembly(BAID: number, updateData: any) {
    return this.updateBinderAssembly(BAID, updateData).pipe(
      tap((updatedAssembly: BinderAssembly) => {
        this.updateAssemblyLocally(BAID, updatedAssembly);
      }),
      catchError(error => {
        console.error('Error updating assembly:', error);
        return throwError(() => new Error('Error updating assembly'));
      })
    );
  }

  getBinderAssemblies(binderId: number): Observable<BinderAssembly[]> {
    return this.http.get<BinderAssembly[]>(`${this.baseUrl}/binderassemblies/${binderId}`);
  }

  updateBinderAssembly(BAID: number, changes: Partial<BinderAssembly>): Observable<BinderAssembly> {
    let prismaAssembly: any = {
      Name: changes.Name,
      Description: changes.Description,
      Price: changes.Price,
      Quantity: changes.Quantity,
      Type: changes.Type,
      AID: changes.AID,
      CategoryName: changes.CategoryName,
      PrintOrder: changes.PrintOrder,
      Altered: changes.Altered,
      TaxFree: changes.TaxFree
    }

    if(changes.CategoryID) {
      prismaAssembly.Categories = { connect: { CategoryID: changes.CategoryID } };
    }
    if(changes.BinderID) {
      prismaAssembly.Binders = { connect: { BinderID: changes.BinderID } };
    }

    if(this.appStateService.learningMode()) {
      return of(new BinderAssembly(changes));
    }
    return this.http.put<BinderAssembly>(`${this.baseUrl}/${BAID}`, prismaAssembly);
  }

  addBinderAssembly(newAssembly: Partial<BinderAssembly>): Observable<BinderAssembly>  {
    return this.http.post<BinderAssembly>(`${this.baseUrl}/`, newAssembly);
  }

  deleteBinderAssembly(BAID: number) {
    return this.http.delete<BinderAssembly>(`${this.baseUrl}/${BAID}`);
  }
}
